import {
  type SelectOwnerState,
  type SelectRootSlotProps,
  type SlotComponentProps,
  useFormControlContext,
  useSlotProps,
} from '@mui/base'
import { clsx } from 'clsx'
import { type JSX } from 'react'

import { IconCaretDown } from '@redteclab/icons'

export type SelectRootProps<
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
> = SelectRootSlotProps<OptionValue, Multiple> & {
  slotProps?: {
    icon?: SlotComponentProps<
      'svg',
      void,
      SelectOwnerState<OptionValue, Multiple>
    >
    root?: SlotComponentProps<
      'button',
      void,
      SelectOwnerState<OptionValue, Multiple>
    >
    value?: SlotComponentProps<
      'span',
      void,
      SelectOwnerState<OptionValue, Multiple>
    >
  }
}

export const SelectRoot = <
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
>(
  props: SelectRootProps<OptionValue, Multiple>,
): JSX.Element => {
  const { children, ownerState, ref, slotProps, ...rest } = props

  const formControlContext = useFormControlContext()

  const rootProps = useSlotProps({
    additionalProps: {
      ref,
    },
    className: clsx(
      'select-root',
      Boolean(formControlContext) && 'form-control__select-root',
    ),
    elementType: 'button',
    externalForwardedProps: rest,
    externalSlotProps: slotProps?.root,
    ownerState,
  })

  const valueProps = useSlotProps({
    className: clsx(
      'select-root__value',
      Boolean(formControlContext) && 'form-control__select-root-value',
    ),
    elementType: 'span',
    externalSlotProps: slotProps?.value,
    ownerState,
  })

  const iconProps = useSlotProps({
    className: 'select-root__icon',
    elementType: 'svg',
    externalSlotProps: slotProps?.icon,
    ownerState,
  })

  return (
    <button type="button" {...rootProps}>
      <span {...valueProps}>{children}</span>
      <IconCaretDown {...iconProps} />
    </button>
  )
}
